'use client';

import { cn } from '@repo/ui';
import { type JSX, useMemo } from 'react';
import { AudioIcon } from './audio-icon';
import type { IKaraokeCachedLoader } from './karaoke-cached-types';
import { KaraokeText } from './karaoke-text';
import { splitWords } from './split-words';
import { useActiveWordIndex } from './use-active-word-index';
import { useKaraokeAudio } from './use-karaoke-audio';

interface IKaraokeBlockProps {
  text: string;
  initialAudioUrl: string | null;
  loadAudio: IKaraokeCachedLoader;
  label: string;
  textClassName?: string;
  wrapperClassName?: string;
  dir?: 'ltr' | 'rtl';
  showInlineIcon?: boolean;
}

export function KaraokeBlock({
  text,
  initialAudioUrl,
  loadAudio,
  label,
  textClassName,
  wrapperClassName,
  dir,
  showInlineIcon = true,
}: IKaraokeBlockProps): JSX.Element {
  const { audioRef, wordTimings, isPlaying, isLoading, toggle, audioHandlers } =
    useKaraokeAudio(loadAudio);
  const words = useMemo(() => splitWords(text), [text]);
  const active = useActiveWordIndex(audioRef, wordTimings, words);
  const interactive = Boolean(initialAudioUrl);

  return (
    <div className={cn('space-y-1', wrapperClassName)}>
      {interactive ? (
        <button
          aria-label={label}
          className={cn(
            'block w-full cursor-pointer rounded-md text-start transition-colors hover:bg-foreground/[0.04] focus-visible:outline-2 focus-visible:outline-foreground/40',
            textClassName,
            isPlaying && 'bg-foreground/[0.04]',
          )}
          dir={dir}
          onClick={toggle}
          type="button"
        >
          {showInlineIcon && (
            <AudioIcon
              className="me-1.5 translate-y-0.5"
              isLoading={isLoading}
              isPlaying={isPlaying}
            />
          )}
          <KaraokeText active={active} words={words} />
        </button>
      ) : (
        <p className={textClassName} dir={dir}>
          <KaraokeText active={active} words={words} />
        </p>
      )}
      {initialAudioUrl && (
        <audio preload="none" ref={audioRef} src={initialAudioUrl} {...audioHandlers}>
          <track kind="captions" />
        </audio>
      )}
    </div>
  );
}
